/* eslint-disable @next/next/next-script-for-ga */
"use client";
import useShouldFire from "./hooks/useShouldFire";
import Script from "next/script";
import useDeviceCapability from "@/hooks/useDeviceCapability";
import useTagStages from "./useTagStages";

interface IPixelsHeadScripts {
  SMARTLOOK_ID: number | string;
  GA_TRACKING_ID: string;
  GTM_ID: string;
  X_PIXEL_ID?: string;
}

/**
 * Third-party tag installation.
 *
 * PERF — why the scripts are released in stages. Every tag here is already
 * `strategy="lazyOnload"` (so it waits for `window.load`) and the whole component is
 * behind the shared analytics gate. That was not enough: the gate released all of
 * them in ONE frame, so GTM, GA, the tenant's server-side GTM container, the X pixel
 * and the Smartlook recorder all started executing together, colliding with our own
 * hydration. Measured on the home page (4x CPU / Slow 4G): 67 long tasks totalling
 * 7,829ms, 83% of all long-task time, and 90% of scroll script time.
 *
 * `useTagStages` hands out one stage per idle slice instead — but ONLY for the tags
 * that are safe to delay:
 *
 *   immediate  GTM (tenant + platform) and Google Analytics. Attribution-critical:
 *              everything downstream depends on `dataLayer` existing, and a shopper
 *              who converts in the first seconds must not be lost. These render as
 *              soon as the gate opens, exactly as they did before.
 *   stage 1    X / Twitter pixel
 *   stage 2    Smartlook recorder — the most expensive and least conversion-critical
 *
 * An earlier version of this put GTM and GA behind the queue too. That was wrong: the
 * queue hands out one slice per task with a 1s worst case each, so on a busy main
 * thread an attribution tag could start seconds late — indistinguishable from broken.
 * Only defer what can afford to be deferred.
 *
 * Nothing here changes WHETHER a tag fires, only when it starts.
 */
const STAGES = 2;

const PixelsHeadScripts = ({
  SMARTLOOK_ID,
  GA_TRACKING_ID,
  GTM_ID,
  X_PIXEL_ID,
}: IPixelsHeadScripts) => {
  const shouldFire = useShouldFire();
  const { isLowEnd } = useDeviceCapability();
  const OSOSS_GTM_ID = process.env.NEXT_PUBLIC_OSOSS_GTM_ID ?? "";
  const stage = useTagStages(STAGES, shouldFire);

  if (!shouldFire) return null;

  return (
    <>
      {/* ── Immediate · Google Tag Manager ─────────────────────────────────────
          NOTE: both the tenant container and the platform container can be
          configured at once, and each creates `dataLayer` and fetches `gtm.js`.
          Downstream tags can then fire twice. Deliberately NOT changed here —
          picking one is a marketing/attribution decision, not a perf one. */}
      {GTM_ID && (
        <Script
          strategy="lazyOnload"
          id="google-tag-head-script"
          dangerouslySetInnerHTML={{
            __html: `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${GTM_ID}');
      `,
          }}
        />
      )}
      {OSOSS_GTM_ID && (
        <Script
          strategy="lazyOnload"
          id="OSOSS-google-tag-head-script"
          dangerouslySetInnerHTML={{
            __html: `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${OSOSS_GTM_ID}');
      `,
          }}
        />
      )}

      {/* ── Immediate · Google Analytics ───────────────────────────────────────
          Loader and init are both immediate: `gtag()` only queues into dataLayer,
          so there is nothing to gain by separating them. */}
      {GA_TRACKING_ID && (
        <Script
          strategy="lazyOnload"
          src={`https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`}
        />
      )}
      {GA_TRACKING_ID && (
        <Script
          strategy="lazyOnload"
          id="gtag-init"
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GA_TRACKING_ID}', {
              page_path: window.location.pathname,
            });
          `,
          }}
        />
      )}

      {/* ── Stage 1 · X (Twitter) pixel ──────────────────────────────────────
          Was `afterInteractive` while everything else was `lazyOnload`, so it
          jumped the queue for no stated reason. Now uniform. */}
      {stage >= 1 && X_PIXEL_ID && (
        <Script
          strategy="lazyOnload"
          id="x-pixel-head-script"
          dangerouslySetInnerHTML={{
            __html: `
            !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
},s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
twq('config',${X_PIXEL_ID});
          `,
          }}
        />
      )}

      {/* ── Stage 2 · Smartlook ──────────────────────────────────────────────
          Last on purpose: the session recorder continuously observes DOM mutations
          and captures screenshots — the heaviest persistent CPU/memory cost on a
          1-2GB Android — and it is the least conversion-critical tag here. Still
          skipped entirely on low-end devices; event analytics above run everywhere. */}
      {stage >= 2 && SMARTLOOK_ID && !isLowEnd && (
        <Script
          strategy="lazyOnload"
          id="SMARTLOOK-head-script"
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `   window.smartlook||(function(d) {
              var o=smartlook=function(){ o.api.push(arguments)},h=d.getElementsByTagName('head')[0];
              var c=d.createElement('script');o.api=new Array();c.async=true;c.type='text/javascript';
              c.charset='utf-8';c.src='https://rec.smartlook.com/recorder.js';h.appendChild(c);
            })(document);
            smartlook('init', '${SMARTLOOK_ID}');`,
          }}
        />
      )}
    </>
  );
};
export default PixelsHeadScripts;
